import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export type DeviceKind = "camera" | "mic" | "speaker";

/**
 * Switch camera, microphone or speaker without leaving the call. Devices could
 * only be picked on the pre-join screen before, and the speaker choice made
 * there was thrown away.
 */
export function DeviceSettingsDialog({
    open,
    onOpenChange,
    audioOnly,
    cameraId,
    micId,
    speakerId,
    onSelect
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    audioOnly: boolean;
    cameraId?: string;
    micId?: string;
    speakerId?: string;
    onSelect: (kind: DeviceKind, deviceId: string) => void;
}) {
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        void navigator.mediaDevices.enumerateDevices()
            .then((d) => { if (!cancelled) setDevices(d); })
            .catch(() => { if (!cancelled) setDevices([]); });
        return () => { cancelled = true; };
    }, [open]);

    const speakersSupported = typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="border-white/10 bg-zinc-900 text-white sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Device settings</DialogTitle>
                    <DialogDescription className="text-white/60">
                        Changes apply straight away — you stay in the call.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    {!audioOnly ? (
                        <Picker
                            label="Camera"
                            devices={devices.filter((d) => d.kind === "videoinput")}
                            value={cameraId}
                            onChange={(id) => onSelect("camera", id)}
                        />
                    ) : null}
                    <Picker
                        label="Microphone"
                        devices={devices.filter((d) => d.kind === "audioinput")}
                        value={micId}
                        onChange={(id) => onSelect("mic", id)}
                    />
                    {speakersSupported ? (
                        <Picker
                            label="Speaker"
                            devices={devices.filter((d) => d.kind === "audiooutput")}
                            value={speakerId}
                            onChange={(id) => onSelect("speaker", id)}
                        />
                    ) : (
                        <p className="text-xs text-white/50">This browser plays call audio through the system's default speaker.</p>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Picker({ label, devices, value, onChange }: {
    label: string;
    devices: MediaDeviceInfo[];
    value: string | undefined;
    onChange: (deviceId: string) => void;
}) {
    return (
        <label className="block">
            <span className="mb-1 block text-xs font-medium text-white/70">{label}</span>
            <select
                value={value ?? devices[0]?.deviceId ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="h-9 w-full rounded-md border border-white/15 bg-zinc-800 px-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            >
                {devices.length === 0 ? <option value="">No device found</option> : null}
                {devices.map((d, i) => (
                    <option key={d.deviceId || i} value={d.deviceId}>
                        {d.label || `${label} ${i + 1}`}
                    </option>
                ))}
            </select>
        </label>
    );
}
